import type { ComponentType } from 'react';
import type { FlowThemeCardMotionFactory } from './cardMotion';
import type { ChromeTone, FlowThemeId, SceneFactory } from './engine/types';

export type LottieData = Record<string, unknown>;

export interface FlowThemeBursts {
  step: LottieData;
  finale: LottieData;
}

/** Admin picker group; the registry lists the groups in order. */
export type FlowThemeCategoryId = 'classic' | 'night' | 'daylight' | 'playful';

/**
 * Frosted glass behind question and completion cards. `dense` is a little more
 * opaque, so text stays crisp over dark backdrops with bright highlights.
 */
export type FlowThemeCardGlass = 'default' | 'dense';

/**
 * Everything the app knows about one theme. Each theme exports one from its
 * folder in `library/`. The definition itself must stay small, because the
 * registry and admin picker import every definition eagerly; the scene and
 * bursts load on demand through `loadScene` and `loadBursts`.
 */
export interface FlowThemeDefinition {
  id: FlowThemeId;
  /** Optional lightweight DOM labels; use useThemeContext for display data. */
  Overlay?: ComponentType;
  label: string;
  /** One-line description for admin pickers. */
  description: string;
  category: FlowThemeCategoryId;
  /** Which header text colour stays legible at a given progress (0..1). */
  chromeTone: (progress: number) => ChromeTone;
  /** CSS background used as a swatch before a live preview loads. */
  swatch: string;
  cardGlass: FlowThemeCardGlass;
  /** Card transition; leave out to keep the player's default horizontal slide. */
  cardMotion?: FlowThemeCardMotionFactory;
  /** Size in CSS pixels of the Lottie bursts played at the scene anchor. */
  burstSize: number;
  /**
   * The scene holds its frame rate without GPU canvas acceleration, so it keeps
   * animating (at low quality) where software rendering holds other themes to
   * still frames. Set it only for a theme measured at 60fps in software
   * rendering, such as one that draws to a small buffer and blits it once
   * (Pixel Quest). See "Performance" in docs/features/flow-themes.md.
   */
  softwareFriendly: boolean;
  loadScene: () => Promise<SceneFactory>;
  loadBursts: () => Promise<FlowThemeBursts>;
}

type JsonModule = Promise<{ default: unknown }>;

/** Pairs a theme's lazily imported step and finale Lottie files. */
export const loadBurstPair = (step: JsonModule, finale: JsonModule): Promise<FlowThemeBursts> =>
  Promise.all([step, finale]).then(([stepModule, finaleModule]) => ({
    step: stepModule.default as LottieData,
    finale: finaleModule.default as LottieData,
  }));
